import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import RegistrosService, { Registro } from '../../services/registrosService';

/** Segundos -> mm:ss, para o offset dentro da gravação ficar legível. */
const formatarTempo = (segundos?: number) => {
  if (segundos == null) return '-';
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const RegistroDetailPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [registro, setRegistro] = useState<Registro | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Áudio da veiculação: só existe em registro criado pelo audio-matcher.
  const [audio, setAudio] = useState<any | null>(null);
  const [erroAudio, setErroAudio] = useState<string | null>(null);
  const playerRef = React.useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const fetchRegistro = async () => {
      try {
        setLoading(true);
        const data = await RegistrosService.getRegistro(Number(id));
        setRegistro(data);
        setError(null);
        // Busca separada: registro sem rastreio devolve 404 aqui, e isso é normal —
        // não pode derrubar a tela inteira.
        if (data?.gravacao_id) {
          try {
            setAudio(await RegistrosService.getAudioVeiculacao(Number(id)));
          } catch (e) {
            console.error('Erro ao carregar áudio da veiculação:', e);
            setErroAudio('Não foi possível carregar o áudio desta veiculação');
          }
        }
      } catch (err) {
        console.error('Erro ao buscar detalhes do registro:', err);
        setError('Não foi possível carregar os detalhes do registro');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRegistro();
    }
  }, [id]);

  const formatDateTimeDisplay = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleBackToList = () => {
    navigate('/registros');
  };

  return (
    <SidebarLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton 
            onClick={handleBackToList}
            sx={{ 
              mr: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Detalhes do Registro
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <Typography variant="h6" color="error">
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={handleBackToList}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              Voltar para listagem
            </Button>
          </Paper>
        ) : registro ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                {registro.titulo || 'Sem título'}
              </Typography>
              <Box>
                <Tooltip title="Editar">
                  <IconButton
                    color="primary"
                    sx={{ 
                      mr: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                    }}
                    onClick={() => navigate(`/registros/edit/${registro.id}`)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                  <IconButton
                    color="error"
                    sx={{ 
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                    }}
                    onClick={() => {
                      // Implementar lógica de confirmação de exclusão
                      if (window.confirm('Tem certeza que deseja remover este registro?')) {
                        RegistrosService.deleteRegistro(registro.id)
                          .then(() => navigate('/registros'))
                          .catch(err => console.error('Erro ao excluir registro:', err));
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Card 
              elevation={0} 
              sx={{ 
                mb: 4,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {registro.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Data/Hora
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDateTimeDisplay(registro.datahora)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Tipo
                    </Typography>
                    <Chip 
                      label={registro.tipo || '-'} 
                      size="small"
                      sx={{ 
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 'medium',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Duração
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDuration(registro.duracao)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {(audio || erroAudio) && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                  Áudio da Veiculação
                </Typography>
                <Card elevation={0} sx={{ mb: 4, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                  <CardContent>
                    {erroAudio && <Typography color="error" variant="body2">{erroAudio}</Typography>}
                    {audio && (
                      <>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Detectado em <strong>{audio.arquivo}</strong>, aos{' '}
                          <strong>{formatarTempo(audio.offset_segundos)}</strong> da gravação
                          {audio.precisao != null && ` · confiança ${Number(audio.precisao).toFixed(1)}%`}
                        </Typography>
                        <audio
                          ref={playerRef}
                          src={audio.url}
                          controls
                          preload="metadata"
                          style={{ width: '100%', marginTop: 8 }}
                          onLoadedMetadata={() => {
                            // Posiciona no comercial. Sem isto o player abre no
                            // início de uma gravação de até 15 minutos.
                            if (playerRef.current) {
                              playerRef.current.currentTime = Math.max(0, (audio.offset_segundos || 0) - 2);
                            }
                          }}
                        />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              if (playerRef.current) {
                                playerRef.current.currentTime = Math.max(0, (audio.offset_segundos || 0) - 2);
                                playerRef.current.play();
                              }
                            }}
                          >
                            Ouvir o comercial
                          </Button>
                          <Button size="small" href={audio.url} target="_blank" rel="noopener">
                            Baixar gravação completa
                          </Button>
                        </Box>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                          O player começa 2 segundos antes do ponto detectado. Link válido por 1 hora.
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Informações Detalhadas
            </Typography>
            
            <TableContainer component={Paper} elevation={0} sx={{ mb: 4, borderRadius: 2 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ width: '30%', fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Mercado
                    </TableCell>
                    <TableCell>{registro.mercado || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Qualidade
                    </TableCell>
                    <TableCell>{registro.qualidade || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Gênero
                    </TableCell>
                    <TableCell>{registro.genero || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Break
                    </TableCell>
                    <TableCell>{registro.break1 || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Posição
                    </TableCell>
                    <TableCell>{registro.posicao || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Custo
                    </TableCell>
                    <TableCell>
                      {registro.custo 
                        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(registro.custo)
                        : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Precisão
                    </TableCell>
                    <TableCell>{registro.precisao !== null ? `${registro.precisao}%` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Sincronia
                    </TableCell>
                    <TableCell>{registro.sincronia !== null ? `${registro.sincronia}%` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Dispersão
                    </TableCell>
                    <TableCell>{registro.dispersao !== null ? `${registro.dispersao}%` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Origem
                    </TableCell>
                    <TableCell>{registro.origem || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Considerações
                    </TableCell>
                    <TableCell>{registro.consideracoes || '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Relacionamentos
            </Typography>
            
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ width: '30%', fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Emissora
                    </TableCell>
                    <TableCell>
                      {registro.emissora 
                        ? registro.emissora.nome 
                        : registro.emissora_id 
                          ? `ID: ${registro.emissora_id}` 
                          : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Marca
                    </TableCell>
                    <TableCell>
                      {registro.marca 
                        ? registro.marca.nome 
                        : registro.marca_id 
                          ? `ID: ${registro.marca_id}` 
                          : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Programa
                    </TableCell>
                    <TableCell>
                      {registro.programa 
                        ? registro.programa.nome 
                        : registro.programa_id 
                          ? `ID: ${registro.programa_id}` 
                          : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                      Anunciante
                    </TableCell>
                    <TableCell>
                      {registro.anunciante 
                        ? registro.anunciante.nome 
                        : registro.anunciante_id 
                          ? `ID: ${registro.anunciante_id}` 
                          : '-'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.warning.main, 0.05),
            }}
          >
            <Typography variant="h6" color="warning.main">
              Registro não encontrado
            </Typography>
            <Button
              variant="contained"
              onClick={handleBackToList}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              Voltar para listagem
            </Button>
          </Paper>
        )}
      </Box>
    </SidebarLayout>
  );
};

export default RegistroDetailPage; 